import styled, { css } from 'styled-components';
import { colors, numericSpace, r4, s16, s8 } from '@ardoq/design-tokens';
import { Icon, IconName } from '@ardoq/icons';
import { errorCSS, warningCSS } from '@ardoq/style-helpers';
import { text2 } from '@ardoq/typography';

export type InputStyleProps = {
  $isFocused?: boolean;
  $isDisabled?: boolean;
  $hasError?: boolean;
  $hasWarning?: boolean;
  $hasRightIcon?: boolean;
  $hasLeftIcon?: boolean;
  $hideErrorIcon?: boolean;
  $hideWarningIcon?: boolean;
  $alignText?: 'left' | 'center' | 'right';
  $queryComponentLabel?: boolean;
  $hasPrefix?: boolean;
  $hasPostfix?: boolean;
  $hasTransparentBackground?: boolean;
  $hasClearButton?: boolean;
};

const getInputPaddingRight = ({
  $hasError,
  $hasWarning,
  $hasRightIcon,
  $hideErrorIcon,
  $hideWarningIcon,
  $hasClearButton,
}: Pick<
  InputStyleProps,
  | '$hasError'
  | '$hasWarning'
  | '$hasRightIcon'
  | '$hideErrorIcon'
  | '$hideWarningIcon'
  | '$hasClearButton'
>) => {
  const CLEAR_BUTTON_WIDTH = $hasClearButton ? 44 : 0;
  const ICON_WIDTH = 20;
  const ICON_PADDING = 8;
  const ICON_WITH_PADDING = ICON_WIDTH + ICON_PADDING;
  const PADDING_FOR_ICON_AND_ERROR =
    ($hasError && $hideErrorIcon) || ($hasWarning && $hideWarningIcon)
      ? ICON_WITH_PADDING + ICON_PADDING
      : 2 * ICON_WITH_PADDING + ICON_PADDING;
  const PADDING_FOR_ICON = $hideErrorIcon
    ? 2 * ICON_PADDING
    : ICON_WITH_PADDING + ICON_PADDING;

  if (($hasError || $hasWarning) && $hasRightIcon)
    return `${PADDING_FOR_ICON_AND_ERROR + CLEAR_BUTTON_WIDTH}px`;
  if ($hasError || $hasWarning || $hasRightIcon)
    return `${PADDING_FOR_ICON + CLEAR_BUTTON_WIDTH}px`;
  return `${numericSpace.medium + CLEAR_BUTTON_WIDTH}px`;
};

const getInputPaddingLeft = ({
  $hasLeftIcon,
}: Pick<InputStyleProps, '$hasLeftIcon'>) => {
  /* 8px padding left, 24px icon width, 8px padding left */
  if ($hasLeftIcon) return '40px';
  return s16;
};

export const commonInputStyle = css<InputStyleProps>`
  border: none;
  border-radius: ${r4};
  box-shadow: inset 0px 0px 0px 1px ${colors.borderAction};
  ${text2}
  width: 100%;

  &:hover {
    box-shadow: inset 0px 0px 0px 1px ${colors.borderActionHover};
  }

  &&:focus {
    box-shadow: inset 0px 0px 0px 2px ${colors.borderActionActive};
  }

  ${({ $isFocused }) =>
    $isFocused &&
    css`
      && {
        box-shadow: inset 0px 0px 0px 2px ${colors.borderActionActive};
      }
    `}

  ${({ $hasError, $hasWarning }) => !$hasError && $hasWarning && warningCSS}

  ${({ $hasError }) => $hasError && errorCSS}

  padding: 7px ${getInputPaddingRight} 7px ${getInputPaddingLeft};
  ${({ $hasTransparentBackground }) =>
    $hasTransparentBackground &&
    css`
      background-color: transparent;
    `};
`;

export const PresetIcon = styled(Icon)<{ $isDisabled?: boolean }>`
  z-index: 4;
  pointer-events: none;
  top: 7px;
  color: ${({ $isDisabled }) =>
    $isDisabled ? colors.iconActionDisabled : colors.iconAction};
  &:first-child {
    left: ${s8};
  }
  &:last-child {
    right: ${s8};
  }
`;

export const FormErrorIcon = styled(PresetIcon).attrs({
  iconName: IconName.ERROR,
})`
  &&& {
    color: ${colors.iconError};
  }
`;

export const FormWarningIcon = styled(PresetIcon).attrs({
  iconName: IconName.WARNING,
})`
  &&& {
    color: ${colors.iconWarning};
  }
`;

export const InputWrapper = styled.div<InputStyleProps>`
  display: flex;
  position: relative;

  && ${FormErrorIcon}, && ${FormWarningIcon} {
    right: ${({ $hasRightIcon, $hasPostfix }) =>
      $hasRightIcon || $hasPostfix ? '40px' : s8};
  }

  ${PresetIcon} {
    position: absolute;
  }

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      pointer-events: none;
    `}
`;

export const ReadOnlyStyle = styled.div<Pick<InputStyleProps, '$hasLeftIcon'>>`
  ${text2};
  flex: 1;
  padding: 6px ${s16};
  background: ${colors.surfaceStatusNeutral};
  box-shadow: inset 0px 0px 0px 1px ${colors.borderAction};
  white-space: pre-line;
  border-radius: ${r4};
  color: ${colors.textDefault};
  min-height: 32px;
  display: flex;
  align-items: center;

  ${({ $hasLeftIcon }) =>
    $hasLeftIcon &&
    css`
      padding-left: 40px;
    `}
`;
